"use client";

import Link from "next/link";
import { ArrowRight, Scale, Shield, FileCheck, Lock, Award, AlertTriangle, ChevronRight } from "lucide-react";
import {
  RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis,
} from "recharts";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  legalStats, legalChildren, legalFeatures, legalUseCases,
  legalWhy, legalFaq, legalCta, complianceFrameworks, slaTiers,
} from "@/lib/data/legal-hub";

/* ============================================================
   BESPOKE 1 — Compliance shield signature diagram.
   Central shield with concentric rings — each ring
   represents a compliance framework with coverage %.
   ============================================================ */
function ComplianceShieldDiagram() {
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  const cx = 260;
  const cy = 260;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="lCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.85" />
            <stop offset="60%" stopColor="#06b6d4" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lShield" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        <circle cx={cx} cy={cy} r="200" fill="url(#lCore)" />
        {/* Concentric coverage rings */}
        {[110, 150, 195, 240].map((r, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke="rgb(255 255 255 / 0.07)" strokeWidth="0.6" strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"} />
        ))}
        {/* Compliance framework nodes around shield */}
        {complianceFrameworks.slice(0, 8).map((fw, i) => {
          const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
          const r = 200;
          const x = ROUND(cx + Math.cos(angle) * r);
          const y = ROUND(cy + Math.sin(angle) * r);
          return (
            <g key={fw.name}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke={`var(--color-${fw.accent})`} strokeOpacity="0.3" strokeWidth="0.6" strokeDasharray="2 4" />
              <circle cx={x} cy={y} r="28" fill={`color-mix(in oklch, var(--color-${fw.accent}) 22%, transparent)`} stroke={`var(--color-${fw.accent})`} strokeWidth="0.8" />
              <circle cx={x} cy={y} r="22" fill="rgb(10 10 18 / 0.78)" />
              <text x={x} y={y - 2} textAnchor="middle" fontSize="6" fontWeight="800" fill={`var(--color-${fw.accent})`} letterSpacing="0.3">
                {fw.coverage}%
              </text>
              <text x={x} y={y + 7} textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.7)" letterSpacing="0.3">
                {fw.name.split(" ")[0]?.toUpperCase()}
              </text>
            </g>
          );
        })}
        {/* Center shield */}
        <g transform={`translate(${cx}, ${cy})`}>
          <path
            d="M 0 -68 L 56 -42 L 56 14 Q 56 50 0 70 Q -56 50 -56 14 L -56 -42 Z"
            fill="url(#lShield)"
            stroke="rgb(255 255 255 / 0.5)"
            strokeWidth="1"
          />
          <path
            d="M 0 -55 L 45 -34 L 45 11 Q 45 41 0 57 Q -45 41 -45 11 L -45 -34 Z"
            fill="rgb(10 10 18 / 0.92)"
          />
          <text x="0" y="-15" textAnchor="middle" fontSize="18" fontWeight="900" fill="white" letterSpacing="-0.5">
            ZERO
          </text>
          <text x="0" y="3" textAnchor="middle" fontSize="18" fontWeight="900" fill="white" letterSpacing="-0.5">
            INCIDENTS
          </text>
          <text x="0" y="22" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#10b981" letterSpacing="2">
            15+ YEARS
          </text>
          <text x="0" y="34" textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            8 FRAMEWORKS
          </text>
        </g>
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <Shield className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">Zero Incidents</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <FileCheck className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">8 Frameworks</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Lock className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">GDPR / CCPA</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <Award className="h-3.5 w-3.5" style={{ color: "var(--color-amber)" }} />
        <span className="text-xs font-semibold">ISO 27001</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Compliance coverage radial chart.
   Shows each framework's coverage percentage.
   ============================================================ */
function ComplianceCoverageChart() {
  const data = complianceFrameworks.map((fw) => ({
    name: fw.name,
    coverage: fw.coverage,
    fill: `var(--color-${fw.accent})`,
  }));
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <Reveal key={d.name} delay={i * 50}>
          <div className="glass rounded-xl p-4" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${d.fill} 12%, transparent)` }}>
            <div className="flex items-center justify-between mb-2 gap-3">
              <span className="text-sm font-semibold flex-1">{d.name}</span>
              <span
                className="text-[10px] font-bold tracking-wider px-2 py-1 rounded"
                style={{
                  color: d.fill,
                  background: `color-mix(in oklch, ${d.fill} 12%, transparent)`,
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${d.fill} 25%, transparent)`,
                }}
              >
                {complianceFrameworks[i]?.status.toUpperCase()}
              </span>
              <span className="text-sm font-bold font-mono-num w-12 text-right" style={{ color: d.fill }}>
                {d.coverage}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${d.coverage}%`,
                  background: `linear-gradient(90deg, ${d.fill}, color-mix(in oklch, ${d.fill} 50%, transparent))`,
                }}
              />
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — SLA response tier table.
   ============================================================ */
function SlaResponseTiers() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[rgb(var(--border)/0.15)]">
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider">Tier</th>
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider">Description</th>
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider">Response</th>
            <th className="text-left py-4 px-4 font-bold text-xs uppercase tracking-wider">Resolution</th>
          </tr>
        </thead>
        <tbody>
          {slaTiers.map((t) => (
            <tr
              key={t.tier}
              className="border-b border-[rgb(var(--border)/0.08)] hover:bg-[rgb(var(--surface)/0.4)] transition-colors"
            >
              <td className="py-4 px-4">
                <div className="flex items-center gap-2">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: `var(--color-${t.accent})` }}
                  />
                  <span className="font-bold text-xs" style={{ color: `var(--color-${t.accent})` }}>
                    {t.tier}
                  </span>
                </div>
              </td>
              <td className="py-4 px-4 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{t.description}</td>
              <td className="py-4 px-4 font-bold font-mono-num text-sm" style={{ color: `var(--color-${t.accent})` }}>
                {t.response}
              </td>
              <td className="py-4 px-4 font-bold font-mono-num text-sm" style={{ color: `var(--color-${t.accent})` }}>
                {t.resolution}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function LegalHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="emerald"
      featuresWrap="crimson"
      useCasesWrap="purple"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Legal & Compliance</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    Legal &{" "}
                    <span className="bg-gradient-to-br from-[var(--color-emerald)] via-[var(--color-cyan)] to-[var(--color-purple)] bg-clip-text text-transparent">
                      Compliance Surfaces
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    Privacy, terms, security, compliance, acceptable use, cookies, data processing, and SLA. Eight legal surfaces that govern every Perception X2 engagement.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    Built for transparency, auditability, and protection. SOC 2 Type II, ISO 27001, GDPR, CCPA, and defense-grade certifications — zero security incidents in 15+ years.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/legal/security"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-emerald)/30]"
                    >
                      View Security Posture <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/legal/compliance"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Compliance Frameworks
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {legalStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <ComplianceShieldDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="Eight Legal Surfaces"
          title="Every Page of Legal & Compliance"
          description="The legal hub gathers every legal surface that governs the Perception X2 platform — from privacy and terms to compliance frameworks, data processing, and contractual SLA."
          items={legalChildren}
          accent="emerald"
        />
      }
      features={
        <>
          <Section id="compliance" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Compliance Coverage"
                title="Eight Frameworks. Continuous Verification."
                description="Coverage percentage and certification status across the eight compliance frameworks that govern the Perception X2 platform. All metrics verified by annual third-party audit."
                accent="crimson"
              />
              <ComplianceCoverageChart />
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Legal & Compliance Capabilities"
                title="What the Legal Stack Delivers"
                description="Eight foundational capabilities shared across every legal surface — each engineered to protect clients, ensure compliance, and maintain audit-grade lineage."
                accent="purple"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {legalFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Legal Applications"
          title="Six Compliance Patterns"
          description="The Perception X2 legal stack supports six primary compliance and assurance patterns — from GDPR review to defense and sovereign engagement."
          cases={legalUseCases}
          accent="purple"
        />
      }
      why={
        <>
          <Section id="sla" className="relative">
            <Container>
              <SectionHeading
                eyebrow="SLA Response Tiers"
                title="Contractual Response & Resolution Targets"
                description="Four SLA tiers govern the Perception X2 platform. Response time is the maximum delay from incident report to first acknowledgement. Resolution time is the maximum delay from report to verified fix."
                accent="cyan"
              />
              <div className="glass rounded-2xl p-6">
                <SlaResponseTiers />
              </div>
            </Container>
          </Section>
          <Section id="why" className="relative mt-8">
            <Container>
              <HubWhy
                eyebrow="Why These Legal Surfaces"
                title="The Trust Differentiators That Compound"
                items={legalWhy}
                accent="cyan"
              />
            </Container>
          </Section>
        </>
      }
      faq={
        <HubFAQ
          eyebrow="Legal FAQ"
          title="Frequently Asked Questions"
          items={legalFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={legalCta.eyebrow}
          title={legalCta.title}
          description={legalCta.description}
          primary={legalCta.primary}
          secondary={legalCta.secondary}
          trustItems={legalCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
